import React, { useState, useEffect } from "react";
// import { ChatProvider } from "./context/ChatContext";
import { EvaluationProvider } from "./context/EvaluationContext";
import { UserProvider, useUserDetails } from "./context/UserContext";
import {
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  useClerk,
  useUser,
  useAuth,
} from "@clerk/clerk-react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import Login, { SignUpPage } from "./pages/Login";
import Home from "./pages/Home";
import HomeAuthed from "./pages/HomeAuthed";
import "./App.css";
import toast, { Toaster } from "react-hot-toast";
import UserOnboarding from "./components/UserOnboarding";
import MainContent from "./components/MainContent";
import './styles/StepProgress.css';
import Settings from "./pages/Settings";
// import CreateContent from "./pages/CreateContent.js";
import CreateImage from "./pages/CreateImage.js";
import CreateVideo from "./pages/CreateVideo.js";
import TacoStudio from "./pages/TacoStudio.js";
import InstagramConnectPrompt from './pages/InstagramConnectPrompt';
import InstagramSchedule from './pages/InstagramSchedule';
import Billing from './pages/Billing';
// import ChatInterface from "./components/ChatInterface";
import EvaluateRag from "./components/EvaluateRag";
import Dashboard from "./components/Dashboard";
import NavBar from "./components/NavBar";
import useSessionTimeout from "./hooks/useSessionTimeout";

// Define LoadingSpinner component with jobStatus passed as prop - styled to match Dashboard.js
const LoadingSpinner = ({ jobStatus, message = "Setting up your workspace" }) => (
  <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center relative overflow-hidden">
    {/* Background decorative elements */}
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-10 left-20 w-64 h-64 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse-slow"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
    </div>
    
    <div className="relative z-10 text-center p-8">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mb-6 mx-auto"></div>
      <div className="text-white">
        <h2 className="text-2xl font-bold mb-2">{message}</h2>
        <p className="text-gray-300 mb-6">
          This may take several minutes. Please don't close or refresh this
          window.
        </p>
        {jobStatus && (
          <div className="w-full max-w-md mx-auto">
            <div className="bg-gray-800 rounded-full h-2 mb-4">
              <div 
                className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                style={{width: `${jobStatus.progress}%`}}
              ></div>
            </div>
            <p className="text-gray-300 text-sm">
              {jobStatus.status === 'queued' ? 'Waiting to start...' :
               jobStatus.status === 'processing' ? `Processing: ${jobStatus.progress}%` :
               jobStatus.status === 'completed' ? 'Setup complete!' :
               'Processing...'}
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

// const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// NavBar component has been moved to ./components/NavBar.js

const MemoizedDashboard = React.memo(Dashboard);

// Add this new component for error handling - styled to match Home.js
const ErrorDisplay = ({ message, onRetry, onSignOut }) => (
  <div className="bg-black min-h-screen overflow-hidden flex flex-col">
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center p-8">
        <div className="text-6xl mb-6">❌</div>
        <h2 className="text-2xl font-bold text-white mb-4">Something went wrong</h2>
        <p className="text-gray-300 mb-8 max-w-md">{message || "Error loading user data. Please try again."}</p>
        <div className="flex gap-4 justify-center">
          <button 
            onClick={onRetry} 
            className="bg-white text-black px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Try Again
          </button>
          <button 
            onClick={onSignOut} 
            className="bg-transparent border border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-black transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Session timeout notification component
const SessionTimeoutNotification = ({ remainingTime, onExtendSession, onSignOut }) => {
  const hours = Math.floor(remainingTime / (1000 * 60 * 60));
  const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);
  
  const timeString = hours > 0 
    ? `${hours}h ${minutes}m remaining`
    : minutes > 0 
    ? `${minutes}m ${seconds}s remaining`
    : `${seconds}s remaining`;

  return (
    <div className="fixed top-4 right-4 z-50 bg-yellow-600 text-white p-4 rounded-lg shadow-lg max-w-sm">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Session Expiring Soon</h3>
          <p className="text-sm">{timeString}</p>
        </div>
        <div className="flex gap-2 ml-4">
          <button
            onClick={onExtendSession}
            className="bg-white text-yellow-600 px-3 py-1 rounded text-sm font-medium hover:bg-gray-100"
          >
            Extend
          </button>
          <button
            onClick={onSignOut}
            className="bg-transparent border border-white text-white px-3 py-1 rounded text-sm font-medium hover:bg-white hover:text-yellow-600"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

// Add this new component for Home with authentication
const HomeWithAuth = () => {
  const { userDetails, userExists, isLoading, fetchFailed, handleRetryFetch } = useUserDetails();
  const { isLoaded, isSignedIn } = useAuth();
  const { signOut } = useClerk();

  // Session timeout management
  const {
    isExpired,
    remainingTime,
    formattedTime,
    isAboutToExpire,
    updateActivity,
    clearSession
  } = useSessionTimeout({
    onSessionExpired: () => {
      toast.error("Your session has expired. Please sign in again.");
      clearSession();
      signOut();
    },
    onSessionAboutToExpire: () => {
      toast.warning("Your session will expire soon. Click to extend your session.");
    }
  });

  // Show loading spinner while checking authentication
  if (!isLoaded || (isSignedIn && isLoading)) {
    return <LoadingSpinner message="Loading..." />;
  }

  // Show error display when fetch failed for signed-in users
  if (fetchFailed && isSignedIn) {
    return (
      <ErrorDisplay
        message="Login is not authenticated. Please sign out and try again."
        onRetry={handleRetryFetch}
        onSignOut={signOut}
      />
    );
  }

  // Always show Home component regardless of authentication status
  // The authentication checks are done, userDetails are available if signed in
  return (
    <>
      <Home />
      {isAboutToExpire && isSignedIn && (
        <SessionTimeoutNotification
          remainingTime={remainingTime}
          onExtendSession={updateActivity}
          onSignOut={signOut}
        />
      )}
    </>
  );
};

function App() {
  const { userDetails, userExists, isLoading, fetchFailed, handleRetryFetch } = useUserDetails();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [jobStatus, setJobStatus] = useState(null);
  const [lastJobId, setLastJobId] = useState(null);
  const { signOut } = useClerk();

  // Clear session storage when user logs out and on browser/tab close
  const prevSignedInRef = React.useRef(isSignedIn);
  useEffect(() => {
    if (!isLoaded) return;
    const wasSignedIn = prevSignedInRef.current;
    if (wasSignedIn && !isSignedIn) {
      try { sessionStorage.clear(); } catch (_) {}
    }
    prevSignedInRef.current = isSignedIn;
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      try { sessionStorage.clear(); } catch (_) {}
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Session timeout management for the main app
  const {
    isExpired,
    remainingTime,
    formattedTime,
    isAboutToExpire,
    updateActivity,
    clearSession
  } = useSessionTimeout({
    onSessionExpired: () => {
      toast.error("Your session has expired. Please sign in again.");
      clearSession();
      signOut();
    },
    onSessionAboutToExpire: () => {
      toast.warning("Your session will expire soon. Click to extend your session.");
    }
  });

  // Modified check job status function
  const checkJobStatus = async () => {
    if (!lastJobId) return;

    try {
      const token = await getToken()
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/check_job_status/${lastJobId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();

      if (data.status === "completed") {
        setLastJobId(null); // Clear the job ID
        toast.dismiss(); // Remove existing toast
      } else {
        // Still processing - show/refresh the persistent toast
        toast.dismiss();
        toast.loading(
          "We are setting up all your data. You can get going in few minutes...",
          {
            duration: 3000,
          }
        );
      }
    } catch (error) {
      console.error("Error checking job status:", error);
    }
  };

  // Modified handleUserAction to allow navigation while checking status
  const handleUserAction = async (actionCallback) => {
    // Execute the action immediately
    actionCallback();

    // Check job status after action (if there's an ongoing job)
    if (lastJobId) {
      await checkJobStatus();
    }
  };

  // Modified conditional rendering to redirect to UserOnboarding when fetchFailed
  if (!isLoaded || isLoading) {
    return <LoadingSpinner message="Loading..." jobStatus={jobStatus} />;
  }

  // Show error display when fetch failed (including 401 errors)
  if (fetchFailed && isSignedIn) {
    return (
      <ErrorDisplay
        message="Login is not authenticated. Please sign out and try again."
        onRetry={handleRetryFetch}
        onSignOut={signOut}
      />
    );
  }

  // Show job processing status if there's an active job
  if (lastJobId && jobStatus && jobStatus.status !== "completed") {
    return <LoadingSpinner jobStatus={jobStatus} message="Setting up your account" />;
  }

  return (
    <div className="bg-black min-h-screen overflow-hidden flex flex-col">
      {/* // <ChatProvider> */}
        <EvaluationProvider>
          <BrowserRouter>
            <Routes>
              {/* Home with authentication checks - show Home page regardless of auth status */}
              <Route path="/" element={<HomeWithAuth />} />

              {/* App entry point route - handles authenticated user routing */}
              <Route
                path="/app"
                element={
                  <>
                    <SignedIn>
                      {!isLoaded ? (
                        <LoadingSpinner />
                      ) : !userExists ? (
                        // <UserOnboarding
                        //   setLastJobId={setLastJobId}
                        //   setUserExists={setUserExists}
                        //   isLoading={isLoading}
                        // />
                        <Navigate to="/home" replace />

                      ) : (
                        <Navigate to="/home" replace />
                      )}
                    </SignedIn>
                    <SignedOut>
                      <RedirectToSignIn />
                    </SignedOut>
                  </>
                }
              />

              {/* Protected routes wrapper */}
              <Route
                element={
                  <>
                    <SignedIn>
                      <MainContent 
                        handleUserAction={handleUserAction}
                        NavBar={NavBar}
                        userDetails={userDetails}
                      />
                      {isAboutToExpire && (
                        <SessionTimeoutNotification
                          remainingTime={remainingTime}
                          onExtendSession={updateActivity}
                          onSignOut={signOut}
                        />
                      )}
                    </SignedIn>
                    <SignedOut>
                      <RedirectToSignIn />
                    </SignedOut>
                  </>
                }
              >
                {/* Nested protected routes */}
                {/* <Route path="/old-dashboard" element={<SocialMediaForm />} /> */}
                <Route path="/home" element={<HomeAuthed />} />
                <Route path="/dashboard" element={<MemoizedDashboard user={user} />} />
                <Route path="/image" element={<CreateImage />} />
                <Route path="/video" element={<CreateVideo />} />
                <Route path="/studio" element={<TacoStudio />} />
                {/* <Route path="/chat" element={<ChatInterface userDetails={userDetails} />} /> */}
                {/* <Route path="/settings" element={<Settings />} /> */}
                <Route path="/billing" element={<Billing />} />
                {/* <Route path="/evaluate-rag" element={<EvaluateRag />} /> */}
              </Route>

              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/sign-up" element={<SignUpPage />} />
              {/* Redirect /signin to /login (for Clerk default redirect) */}
              <Route path="/signin" element={<Navigate to="/login" replace />} />
              {/* <Route path="/InstagramConnectPrompt" element={<InstagramConnectPrompt />} />
              <Route path="/instagram-schedule" element={<InstagramSchedule />} /> */}
            </Routes>
          </BrowserRouter>
        </EvaluationProvider>
      {/* // </ChatProvider> */}
    </div>
  );
}

// Wrapper component to provide UserContext
const AppWithContext = () => {
  return (
    <UserProvider>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1F2937',
            color: '#fff',
            border: '1px solid #374151',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <App />
    </UserProvider>
  );
};

export default AppWithContext;
